import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import {SnackBarService} from '@q9elements/ui-kit/common';
import {findIndex} from 'lodash';
import moment from 'moment';
import {BehaviorSubject} from 'rxjs';
import {
  SingleUseColumnsDisplayNames,
  SingleUseSnakeCaseColumns
} from '../../../../models/consts/SingleUseCustomViewColumns';

import {ICustomView, ICustomViewFilter} from '../../../../models/custom-view.interface';
import {CustomViewService} from '../../../../services/custom-view.service';
import {GridEditTemplate} from '../../dialogs/models/create-new-view.enum';
import {IColumn} from '../../dialogs/models/create-new-view.interface';

const MAX_COMPOUND_FILTERS = 10;
const GLOW_ANIMATION_DURATION = 1500;

@Component({
  selector: 'app-add-filters-view',
  templateUrl: './add-filters-view.component.html',
  styleUrls: ['./add-filters-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddFiltersViewComponent implements OnInit, OnDestroy {
  @Input() private mode = 'creating';
  @Input() private view: ICustomView;
  @Input() public closeBtn = 'POPUPS.SWITCH_BACK';
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  @Output() public submitForm: EventEmitter<ICustomViewFilter[]> = new EventEmitter<
    ICustomViewFilter[]
  >();
  @Output() public cancelFiltersSelect: EventEmitter<void> = new EventEmitter<void>();

  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public filterListForm: UntypedFormGroup;
  public newFilterFormControl: UntypedFormControl;
  public newFilterPresetControl: UntypedFormControl;
  public combinedFilter: Record<string, boolean> = {};
  public combinedFilterDictionary: Record<string, {control: any; at: number}[]> = {};
  public renderedCombinedFilter: Record<string, boolean> = {};

  public columns: IColumn[];
  public columnsWithoutFilters: IColumn[];
  public glowCard: boolean = false;

  private readonly snackBarService = inject(SnackBarService);
  private selectedColumns: IColumn[];

  get filters() {
    return this.filterListForm.get('filters') as UntypedFormArray;
  }

  private get filterValues(): ICustomViewFilter[] {
    return this.filters.controls.map(({value}) => ({...value}));
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private customViewService: CustomViewService
  ) {}

  ngOnInit(): void {
    this._initFiltersListForm();

    if (this.mode === 'editing') {
      this._initEditingMode();
    } else {
      this._initCreatingMode();
    }

    this._initCombinedFilters();
    this._initCombinedFiltersList();
  }

  ngOnDestroy(): void {
    if (this.mode !== 'editing') {
      this.customViewService.storeFilters(this.filterValues);
    }
  }

  private _initCombinedFilters(): void {
    const columnCount = {};

    this.filters.controls.forEach(control => {
      const {column} = control.getRawValue();

      columnCount[column] = columnCount[column] || 0;
      columnCount[column] += 1;

      if (columnCount[column] > 1) {
        this.combinedFilter[column] = true;
      }
    });
  }

  private _initCombinedFiltersList(): void {
    this.combinedFilterDictionary = this.filters.controls.reduce(
      (acc, control, index) => {
        const column = control.getRawValue().column;
        if (!acc[column]) {
          acc[column] = [];
        }

        acc[column].push({
          control,
          at: index
        });

        return acc;
      },
      {} as Record<string, {control: any; at: number}[]>
    );
  }

  private _initFiltersListForm() {
    const form = {filters: new UntypedFormArray([])};
    this.newFilterFormControl = new UntypedFormControl();
    this.newFilterPresetControl = new UntypedFormControl();
    this.filterListForm = this.formBuilder.group(form);
  }

  private triggerGlow(): void {
    this.glowCard = true;
    setTimeout(() => {
      this.glowCard = false;
    }, GLOW_ANIMATION_DURATION);
  }

  private _initCreatingMode(): void {
    this.selectedColumns = this.retrieveFilterableSelectedColumns();
    this.columns = this.selectedColumns.slice();
    this.columnsWithoutFilters = this.selectedColumns.slice();

    // Seed only filters whose column is still filterable — the selection may have
    // changed since they were stored (e.g. via stepper-header navigation).
    const preservedFilters = this.customViewService
      .retrieveFilters()
      .filter(filter => this.columns.some(column => column.field.value === filter.column));
    if (preservedFilters?.length) {
      for (const filter of preservedFilters) {
        this.filters.push(new UntypedFormControl(filter));
      }
      this.updateColumnsWithoutFilter();
    }

    this.loading$.next(false);
  }

  private _initEditingMode(): void {
    const {filters} = this.view;

    this.setUpColumnsForEdit(filters);

    for (const filter of filters) {
      this.filters.push(new UntypedFormControl(filter));
    }

    this.loading$.next(false);
  }

  public isCombined(filter): boolean {
    return Boolean(this.combinedFilter[filter.getRawValue().column]);
  }

  public getFreshFilters(): any {
    this.renderedCombinedFilter = {};
    return this.filters.controls;
  }

  public markRendered(column: string): boolean {
    this.renderedCombinedFilter[column] = true;
    return true;
  }

  public onAdd(): void {
    const {value: filter, invalid} = this.newFilterFormControl;

    if (!filter || invalid) {
      return;
    }

    const existingFilters: number = this.filters.controls.reduce((acc, curr) => {
      if (curr.value.column === filter.column) {
        return acc + 1;
      }

      return acc;
    }, 0);

    if (existingFilters >= MAX_COMPOUND_FILTERS) {
      this.snackBarService.error('RUBIC_CUBE.TOO_MUCH_SAME_FILTER');
      return;
    }

    this.filters.push(new UntypedFormControl(filter));
    this._initCombinedFilters();
    this._initCombinedFiltersList();
    this.newFilterFormControl.setValue(null);
    this.newFilterPresetControl.setValue(null);
    this.updateColumnsWithoutFilter();
  }

  public onRemove(index: number): void {
    this.filters.removeAt(index);
    this._initCombinedFilters();
    this._initCombinedFiltersList();
    this.updateColumnsWithoutFilter();
  }

  public submit() {
    this.submitForm.emit(this.formatDateFilters(this.filterValues));
  }

  private writeNewColumnsWithoutFilter(filters) {
    this.columnsWithoutFilters = this.selectedColumns.filter(column => {
      return (
        !SingleUseColumnsDisplayNames.includes(column.field.displayValue) ||
        findIndex(filters, {column: column.field.value}) === -1
      );
    });
  }

  public isMultiUse(control: UntypedFormControl): boolean {
    return !SingleUseSnakeCaseColumns.includes(control.getRawValue().column);
  }

  private updateColumnsWithoutFilter() {
    if (!this.filters.value.length) {
      this.columnsWithoutFilters = this.selectedColumns.slice();
    } else {
      this.writeNewColumnsWithoutFilter(this.filters.value);
    }
  }

  public cancel() {
    this.cancelFiltersSelect.emit();
  }

  private retrieveFilterableSelectedColumns() {
    return this.customViewService
      .retrieveSelectedColumns()
      .selectedColumns.filter(column => !column.field.columnOnly);
  }

  private formatDateFilters(filters: ICustomViewFilter[]) {
    const dateColumns = this.selectedColumns.filter(
      column => column.editTemplate === GridEditTemplate.date
    );

    return filters.map(filter => {
      const isDateFilter = dateColumns.some(column => column.field.value === filter.column);

      if (isDateFilter) {
        // endOf('day') (not +23:59:59) so re-submitting an already-normalized
        // persisted value is a no-op — the edit flow re-runs this on every save.
        if (filter.operator === 'lt') {
          filter.value = moment(filter.value).endOf('day').toISOString();

          return filter;
        }

        if (filter.operator === 'range') {
          filter.secondValue = moment(filter.secondValue).endOf('day').toISOString();

          return filter;
        }

        filter.value = new Date(filter.value).toISOString();
      }

      if (filter.column === 'node_assignee' && filter.value.toLowerCase() === 'unassigned') {
        filter.value = filter.value.toLowerCase();
      }

      return filter;
    });
  }

  private setUpColumnsForEdit(filters) {
    this.selectedColumns = this.retrieveFilterableSelectedColumns().map(({field}) => ({
      field,
      operator: '',
      editTemplate: field.valueType as GridEditTemplate
    }));

    this.columns = this.selectedColumns.slice();
    this.writeNewColumnsWithoutFilter(filters);
  }

  public addToCombineGroup(filter): void {
    this.triggerGlow();
    this.newFilterPresetControl.setValue({column: filter.column, operator: filter.operator});

    const container = this.scrollContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
  }
}
